/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { register } from '../core/_requests'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from 'Store/Auth'
import PasswordMeter from 'Presentation/Components/PasswordMeter'
import { shallow } from 'zustand/shallow'
import Swal from 'sweetalert2'
import { useAppStore } from 'Store/App'
import IndustrialType from 'Data/industrial-type.json'
import Select from 'react-select'

const initialValues = {
	companyName: '',
	contactPersonFullname: '',
	contactPersonPosition: '',
	supplierType: '',
	email: '',
	password: '',
	acceptTerms: false,
}

const registrationSchemaStep1 = Yup.object().shape({
	email: Yup.string()
		.email('กรุณากรอกอีเมลให้ถูกต้อง')
		.min(3, 'อีเมลต้องมีอย่างน้อย 3 ตัวอักษร')
		.max(50, 'อีเมลต้องไม่เกิน 50 ตัวอักษร')
		.required('กรุณากรอกอีเมล'),
	password: Yup.string()
		.min(8, 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร')
		.max(50, 'รหัสผ่านต้องไม่เกิน 50 ตัวอักษร')
		.required('กรุณากรอกรหัสผ่าน'),
	// changepassword: Yup.string()
	// 	.required('Password confirmation is required')
	// 	.when('password', {
	// 		is: (val: string) => (val && val.length > 0 ? true : false),
	// 		then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
	// 	}),
	// acceptTerms: Yup.bool().required('You must accept the terms and conditions'),
})

const registrationSchemaStep2 = Yup.object().shape({
	companyName: Yup.string()
		.min(3, 'ชื่อบริษัทต้องมีอย่างน้อย 3 ตัวอักษร')
		.max(50, 'ชื่อบริษัทต้องไม่เกิน 50 ตัวอักษร')
		.required('กรุณากรอกชื่อบริษัท'),
	contactPersonFullname: Yup.string()
		.min(3, 'ชื่อผู้ติดต่อต้องมีอย่างน้อย 3 ตัวอักษร')
		.max(50, 'ชื่อผู้ติดต่อต้องไม่เกิน 50 ตัวอักษร')
		.required('กรุณากรอกชื่อผู้ติดต่อ'),
	contactPersonPosition: Yup.string()
		.min(3, 'ตำแหน่งผู้ติดต่อต้องมีอย่างน้อย 3 ตัวอักษร')
		.max(50, 'ตำแหน่งผู้ติดต่อต้องไม่เกิน 50 ตัวอักษร')
		.required('กรุณากรอกตำแหน่งผู้ติดต่อ'),
	supplierType: Yup.string().required('กรุณาเลือกประเภทผู้ขาย'),
})

export function Registration() {
	const [loading, setLoading] = useState(false)
	const [currentStep, setCurrentStep] = useState<number>(0)
	const [showPassword, setShowPassword] = useState(false)
	const validateEmailAvailable = useAuthStore(state => state.validateEmailAvailable, shallow)
	const setHasFetchProfile = useAppStore(state => state.setHasFetchProfile, shallow)
	const { setCookieAccessToken, setCookieRefreshToken } = useAuthStore(
		state => ({
			setCookieAccessToken: state.setCookieAccessToken,
			setCookieRefreshToken: state.setCookieRefreshToken,
		}),
		shallow
	)
	const location = useLocation()
	const searchParams = new URLSearchParams(location.search)
	const ssoEmailRef = searchParams.get('email')

	// const onSignInGoogle = () => {
	// 	window.location.href = `${process.env.REACT_APP_GOOGLE_AUTH_URL}`
	// }

	const supplierTypeOptions = IndustrialType.map(type => ({
		label: `${type.code} ${type.nameTh} (${type.nameEn})`,
		value: type.code,
	}))

	const formik = useFormik({
		initialValues,
		validationSchema: currentStep === 0 ? registrationSchemaStep1 : registrationSchemaStep2,
		onSubmit: async (values, { setStatus, setSubmitting }) => {
			setLoading(true)

			if (currentStep === 0) {
				validateEmailAvailable(values.email).then(({ success, data }) => {
					if (success) {
						setCurrentStep(1)
						setSubmitting(false)
						setLoading(false)
						setStatus(undefined)
					} else {
						setStatus(data ?? 'อีเมลนี้ถูกใช้งานแล้ว')
						setSubmitting(false)
						setLoading(false)
					}
				})
			} else if (currentStep === 1) {
				register(
					values.email,
					values.password,
					values.companyName,
					values.contactPersonFullname,
					values.contactPersonPosition,
					values.supplierType
				)
					.then(({ data }) => {
						Swal.fire({
							icon: 'success',
							title: 'ลงทะเบียนสำเร็จ',
							confirmButtonText: 'ตกลง',
							customClass: {
								confirmButton: 'btn btn-kumopack-primary-600 text-kumopack-base-white',
							},
						}).then(() => {
							const { accessToken, refreshToken } = data
							setCookieAccessToken(accessToken)
							setCookieRefreshToken(refreshToken)
							setHasFetchProfile(false)
						})
					})
					.catch((error: any) => {
						setStatus(
							`สร้างผู้ใช้งานไม่สำเร็จ: ${
								error?.response?.data?.message?.toString() ?? 'เกิดข้อผิดพลาด'
							} `
						)
						setSubmitting(false)
						setLoading(false)
					})
			}
		},
	})

	useEffect(() => {
		if (ssoEmailRef) {
			formik.setFieldValue('email', ssoEmailRef)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ssoEmailRef])

	return (
		<form
			className="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
			noValidate
			id="kt_login_signup_form"
			onSubmit={formik.handleSubmit}>
			{/* begin::Heading */}
			<div className="mb-10 text-center">
				{/* begin::Title */}
				<h1 className="text-dark mb-3">Create an Account</h1>
				{/* end::Title */}

				{/* begin::Link */}
				<div className="text-gray-400 fw-bold fs-4">
					Already have an account?
					<Link
						to="/auth/login"
						className="text-kumopack-primary-700 fw-bolder"
						style={{ marginLeft: '5px' }}>
						Forgot Password ?
					</Link>
				</div>
				{/* end::Link */}
			</div>
			{/* end::Heading */}

			{/* begin::Action */}
			{/* <button
				type="button"
				onClick={onSignInGoogle}
				className="btn btn-kumopack-primary-600 fw-bolder w-100 mb-10 text-kumopack-base-white">
				<img
					alt="Logo"
					src={toAbsoluteUrl('/media/svg/brand-logos/google-icon.svg')}
					className="h-20px me-3"
					style={{ userSelect: 'none', pointerEvents: 'none' }}
				/>
				Sign in with Google
			</button> */}
			{/* end::Action */}

			{/* <div className="d-flex align-items-center mb-10">
				<div className="border-bottom border-gray-300 mw-50 w-100"></div>
				<span className="fw-bold text-gray-400 fs-7 mx-2">OR</span>
				<div className="border-bottom border-gray-300 mw-50 w-100"></div>
			</div> */}

			{formik.status && (
				<div className="mb-lg-15 alert alert-danger">
					<div className="alert-text font-weight-bold">{formik.status}</div>
				</div>
			)}

			{currentStep === 0 && (
				<>
					{/* begin::Form group Email */}
					<div className="fv-row mb-7">
						<label className="form-label fw-bolder text-dark fs-6">Email</label>
						<input
							placeholder="Email"
							type="email"
							autoComplete="off"
							{...formik.getFieldProps('email')}
							className={clsx(
								'form-control form-control-lg form-control-solid',
								{ 'is-invalid': formik.touched.email && formik.errors.email },
								{
									'is-valid': formik.touched.email && !formik.errors.email,
								}
							)}
						/>
						{formik.touched.email && formik.errors.email && (
							<div className="fv-plugins-message-container">
								<div className="fv-help-block">
									<span role="alert">{formik.errors.email}</span>
								</div>
							</div>
						)}
					</div>
					{/* end::Form group */}

					{/* begin::Form group Password */}
					<div className="mb-10 fv-row" data-kt-password-meter="true">
						<div className="mb-1">
							<label className="form-label fw-bolder text-dark fs-6">Password</label>
							<div className="position-relative mb-3">
								<input
									type={showPassword ? 'text' : 'password'}
									placeholder="Password"
									autoComplete="off"
									{...formik.getFieldProps('password')}
									className={clsx(
										'form-control form-control-lg form-control-solid',
										{
											'is-invalid': formik.touched.password && formik.errors.password,
										},
										{
											'is-valid': formik.touched.password && !formik.errors.password,
										}
									)}
									style={{ backgroundImage: 'none' }}
								/>
								<span
									className="btn btn-sm btn-icon btn-active-secondary position-absolute translate-middle top-50 end-0 me-n2"
									onClick={() => setShowPassword(prevState => !prevState)}>
									<img
										src={
											showPassword
												? '/media/icons/kumopack/eye-grey.svg'
												: '/media/icons/kumopack/eye-off-grey.svg'
										}
										alt="Eye Off Grey"
										width={17}
										style={{ userSelect: 'none', pointerEvents: 'none' }}
									/>
								</span>
							</div>
							{/* begin::Meter */}
							<PasswordMeter
								password={formik.values.password}
								minStrength={8}
								thresholdLength={8}
							/>
							{/* end::Meter */}
							{formik.touched.password && formik.errors.password && (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{formik.errors.password}</span>
									</div>
								</div>
							)}
						</div>
						<div className="text-kumopack-grey-500">
							Use 8 or more characters with a mix of letters, numbers & symbols.
						</div>
					</div>
					{/* end::Form group */}

					{/* begin::Form group */}
					<div className="fv-row mb-10">
						<div className="form-check form-check-custom form-check-solid">
							<input
								className={clsx('form-check-input', {
									'bg-kumopack-primary-600 border-kumopack-base-white': formik.values.acceptTerms,
									'border-kumopack-gray-700': !formik.values.acceptTerms,
								})}
								type="checkbox"
								id="kt_login_toc_agree"
								{...formik.getFieldProps('acceptTerms')}
							/>
							<label
								className="form-check-label fw-bold text-gray-700 fs-6"
								htmlFor="kt_login_toc_agree">
								I Agree the{' '}
								<Link to="#" className="ms-1 text-kumopack-primary-700">
									terms and conditions
								</Link>
								.
							</label>
							{formik.touched.acceptTerms && formik.errors.acceptTerms && (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{formik.errors.acceptTerms}</span>
									</div>
								</div>
							)}
						</div>
					</div>
					{/* end::Form group */}
				</>
			)}

			{currentStep === 1 && (
				<>
					<div className="row fv-row mb-7">
						<div className="col-xl 12">
							<label className="form-label fw-bolder text-dark fs-6">
								ขื่อบริษัท / ชื่อร้านค้า
							</label>
							<input
								placeholder="บริษัทจำกัด"
								type="text"
								autoComplete="off"
								{...formik.getFieldProps('companyName')}
								className={clsx(
									'form-control form-control-lg form-control-solid',
									{
										'is-invalid': formik.touched.companyName && formik.errors.companyName,
									},
									{
										'is-valid': formik.touched.companyName && !formik.errors.companyName,
									}
								)}
							/>
							{formik.touched.companyName && formik.errors.companyName && (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{formik.errors.companyName}</span>
									</div>
								</div>
							)}
						</div>
					</div>

					{/* begin::Form group Firstname */}
					<div className="row fv-row mb-7">
						<div className="col-xl-6">
							<label className="form-label fw-bolder text-dark fs-6">ชื่อผู้ติดต่อ</label>
							<input
								placeholder="นาย สมชาย"
								type="text"
								autoComplete="off"
								{...formik.getFieldProps('contactPersonFullname')}
								className={clsx(
									'form-control form-control-lg form-control-solid',
									{
										'is-invalid':
											formik.touched.contactPersonFullname && formik.errors.contactPersonFullname,
									},
									{
										'is-valid':
											formik.touched.contactPersonFullname && !formik.errors.contactPersonFullname,
									}
								)}
							/>
							{formik.touched.contactPersonFullname && formik.errors.contactPersonFullname && (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{formik.errors.contactPersonFullname}</span>
									</div>
								</div>
							)}
						</div>

						<div className="col-xl-6">
							{/* begin::Form group Lastname */}

							<label className="form-label fw-bolder text-dark fs-6">ตำแหน่งของผู้ติดต่อ</label>
							<input
								placeholder="CEO"
								type="text"
								autoComplete="off"
								{...formik.getFieldProps('contactPersonPosition')}
								className={clsx(
									'form-control form-control-lg form-control-solid',
									{
										'is-invalid':
											formik.touched.contactPersonPosition && formik.errors.contactPersonPosition,
									},
									{
										'is-valid':
											formik.touched.contactPersonPosition && !formik.errors.contactPersonPosition,
									}
								)}
							/>
							{formik.touched.contactPersonPosition && formik.errors.contactPersonPosition && (
								<div className="fv-plugins-message-container">
									<div className="fv-help-block">
										<span role="alert">{formik.errors.contactPersonPosition}</span>
									</div>
								</div>
							)}

							{/* end::Form group */}
						</div>
					</div>
					{/* end::Form group */}

					<div className="row fv-row mb-7">
						<div className="col-xl-12">
							<div className="fv-row mb-5">
								<label className="form-label fw-bolder text-dark fs-6">
									ประเภทของร้านค้า/ธุรกิจ
								</label>
								<Select
									isClearable
									isMulti={false}
									isSearchable
									options={supplierTypeOptions}
									styles={{
										control: (baseStyles, state) => ({
											...baseStyles,
											border:
												formik.touched.supplierType && formik.errors.supplierType
													? '1px solid #f1416c'
													: '1px solid #e1e3ea',
										}),
									}}
									components={{
										IndicatorSeparator: () => null,
									}}
									value={
										supplierTypeOptions.find(
											option => option.value === formik.values.supplierType
										) ?? null
									}
									onChange={option => {
										formik.setFieldValue('supplierType', option?.value ?? '')
									}}
								/>
								{formik.touched.supplierType && formik.errors.supplierType && (
									<div className="fv-plugins-message-container">
										<div className="fv-help-block">
											<span role="alert">{formik.errors.supplierType}</span>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</>
			)}

			{/* begin::Form group Confirm password */}
			{/* <div className="fv-row mb-5">
				<label className="form-label fw-bolder text-dark fs-6">Confirm Password</label>
				<input
					type="password"
					placeholder="Password confirmation"
					autoComplete="off"
					{...formik.getFieldProps('changepassword')}
					className={clsx(
						'form-control form-control-lg form-control-solid',
						{
							'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
						},
						{
							'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
						}
					)}
				/>
				{formik.touched.changepassword && formik.errors.changepassword && (
					<div className="fv-plugins-message-container">
						<div className="fv-help-block">
							<span role="alert">{formik.errors.changepassword}</span>
						</div>
					</div>
				)}
			</div> */}
			{/* end::Form group */}

			{/* begin::Form group */}
			<div className="text-center">
				<button
					type="submit"
					id="kt_sign_up_submit"
					className="btn btn-lg btn-kumopack-primary-600 w-100 mb-5 text-kumopack-base-white"
					disabled={
						formik.isSubmitting || !formik.isValid
						// || !formik.values.acceptTerms
					}>
					{!loading && <span className="indicator-label">Submit</span>}
					{loading && (
						<span
							className="indicator-progress text-kumopack-base-white"
							style={{ display: 'block' }}>
							Please wait...{' '}
							<span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					)}
				</button>
				<Link to="/auth/login">
					<button
						type="button"
						id="kt_login_signup_form_cancel_button"
						className="btn btn-lg btn-secondary w-100 mb-5">
						Cancel
					</button>
				</Link>
			</div>
			{/* end::Form group */}
		</form>
	)
}
